import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Check, Headphones, MessageSquareQuote, Trophy } from "lucide-react";
import { sessionDate, sessionTime } from "@/components/junior/SessionChecklist";
import { MediaPlayer } from "@/components/junior/SessionMedia";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WEEK_COLORS = ["from-cyan-300 to-blue-400", "from-lime-300 to-emerald-400", "from-yellow-300 to-orange-400", "from-pink-400 to-fuchsia-500"];

const Ring = ({ pct }) => (
  <div className="relative w-40 h-40" data-testid="progress-ring">
    <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
      <circle cx="18" cy="18" r="15.9" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="3" />
      <motion.circle cx="18" cy="18" r="15.9" fill="none" stroke="url(#g)" strokeWidth="3" strokeLinecap="round" strokeDasharray="100" initial={{ strokeDashoffset: 100 }} animate={{ strokeDashoffset: 100 - pct }} transition={{ duration: 1.2 }} />
      <defs><linearGradient id="g"><stop offset="0%" stopColor="#bef264" /><stop offset="100%" stopColor="#f472b6" /></linearGradient></defs>
    </svg>
    <div className="absolute inset-0 flex flex-col items-center justify-center">
      <span className="font-display text-4xl font-bold text-white" data-testid="progress-pct">{pct}%</span>
      <span className="text-white/50 text-xs">complete</span>
    </div>
  </div>
);

export default function JuniorProgress() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    axios.get(`${API}/junior/progress/${token}`).then((r) => setData(r.data)).catch(() => setError(true));
  }, [token]);

  if (error) return <div className="min-h-screen bg-dream-bg flex items-center justify-center text-white/60 pt-24" data-testid="progress-not-found">This progress link isn&apos;t valid. Please check your email or contact the studio.</div>;
  if (!data) return <div className="min-h-screen bg-dream-bg" />;

  const pct = Math.round((data.completed.length / data.pathway.length) * 100);
  return (
    <div className="min-h-screen bg-dream-bg pt-40 pb-24 relative overflow-hidden" data-testid="junior-progress-page">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(190,242,100,0.15),transparent_50%),radial-gradient(ellipse_at_bottom_right,rgba(244,114,182,0.18),transparent_50%)]" />
      <div className="relative max-w-4xl mx-auto px-6">
        <div className="flex flex-col sm:flex-row items-center gap-8 mb-12">
          <Ring pct={pct} />
          <div className="text-center sm:text-left">
            <p className="font-mono text-xs tracking-[0.3em] uppercase text-lime-300 mb-2">Junior DJ Academy · {data.package}</p>
            <h1 className="font-display text-4xl sm:text-5xl font-bold text-white" data-testid="progress-student-name">{data.student_first_name}&apos;s DJ journey</h1>
            <p className="text-white/60 mt-2">{data.completed.length} of {data.pathway.length} skills unlocked{data.updated_at ? ` · updated ${new Date(data.updated_at).toLocaleDateString("en-AU")}` : ""}</p>
            {pct === 100 && <p className="inline-flex items-center gap-2 mt-3 px-3 py-1 rounded-full bg-yellow-300 text-black text-sm font-bold"><Trophy className="w-4 h-4" /> Pathway complete!</p>}
          </div>
        </div>

        {data.sessions?.length > 0 && (
          <div className="mb-10 rounded-3xl p-6 bg-white/[0.03] border border-white/10" data-testid="progress-sessions">
            <p className="text-white font-semibold mb-4">Sessions · {data.sessions.filter((s) => s.attended).length} of {data.sessions.length} attended</p>
            <ol className="space-y-3">
              {data.sessions.map((s, i) => (
                <li key={i} className="flex gap-3" data-testid={`progress-session-${i}`}>
                  <span className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-xs font-bold ${s.attended ? "bg-lime-300 text-black" : "border border-white/20 text-white/50"}`}>{s.attended ? <Check className="w-4 h-4" /> : i + 1}</span>
                  <div>
                    <p className={`text-sm ${s.attended ? "text-white" : "text-white/50"}`}>Session {i + 1} · {sessionDate(s.date)} · {sessionTime(s.start_hour)} {!s.attended && <span className="text-white/30">· {new Date(`${s.date}T23:59:59`) < new Date() ? "not attended" : "upcoming"}</span>}</p>
                    {s.feedback && <p className="text-white/75 text-sm mt-1 italic" data-testid={`progress-session-feedback-${i}`}>&ldquo;{s.feedback}&rdquo;</p>}
                    {s.media && <MediaPlayer token={token} media={s.media} testId={`progress-session-media-${i}`} />}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-5">
          {[1, 2, 3, 4].map((w) => (
            <div key={w} className="rounded-3xl p-6 bg-white/[0.03] border border-white/10" data-testid={`progress-week-${w}`}>
              <p className={`inline-block px-3 py-1 rounded-full text-black text-xs font-bold bg-gradient-to-r ${WEEK_COLORS[w - 1]} mb-4`}>Week {w}</p>
              <ul className="space-y-3">
                {data.pathway.filter((p) => p.week === w).map((p) => {
                  const on = data.completed.includes(p.id);
                  return (
                    <li key={p.id} className={`flex items-start gap-3 text-sm ${on ? "text-white" : "text-white/40"}`} data-testid={`progress-step-${p.id}`}>
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${on ? "bg-lime-300 text-black" : "border border-white/20"}`}>{on ? <Check className="w-4 h-4" /> : <Headphones className="w-3 h-3" />}</span>
                      {p.title}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        {data.notes.length > 0 && (
          <div className="mt-10 rounded-3xl p-6 bg-gradient-to-br from-pink-500/10 to-lime-300/10 border border-white/10" data-testid="progress-notes">
            <p className="text-white font-semibold flex items-center gap-2 mb-4"><MessageSquareQuote className="w-5 h-5 text-pink-400" /> Coach&apos;s notes</p>
            <ul className="space-y-3">
              {[...data.notes].reverse().map((n) => (
                <li key={n.created_at} className="text-white/80 text-sm"><span className="text-white/40 text-xs block">{new Date(n.created_at).toLocaleDateString("en-AU")}</span>{n.text}</li>
              ))}
            </ul>
          </div>
        )}
        <p className="text-center text-white/40 text-sm mt-12">Questions? <Link to="/contact" className="text-lime-300 hover:underline">Contact the studio</Link></p>
      </div>
    </div>
  );
}
